import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { lazy, ReactNode, Suspense } from "react";
import MenuBar from "./containers/MenuBar";
import { Spinner } from "./components/Spinner";
import { Toaster } from "react-hot-toast";
import { defaultTheme } from "./theme";
import styled, { ThemeProvider } from "styled-components";
import ShoppingCart from "./pages/ShoppingCart";
import { AppProviders, MenuItems, useAuth } from "./contexts";
import Winners from "./pages/Winners";
import AboutUs from "./pages/AboutUs";
import Auctions from "./pages/Auctions";
import Blog from "./pages/Blog";
import Help from "./pages/Help";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import Footer from "./containers/Footer";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const HomePage = lazy(() => import("./pages/HomePage"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const AuctionDetail = lazy(() => import("./pages/AuctionDetail"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const CurrentAuctionDetail = lazy(() => import("./pages/CurrentAuctionDetail"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ConfirmEmail = lazy(() => import("./pages/ConfirmEmail"));

interface LayoutProps {
  children: ReactNode;
}

const Main = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  @media (min-width: 768px) {
    font-size: 16px;
  }
  @media (min-width: 1024px) {
    font-size: 18px;
  }
`;
const Content = styled.div`
  flex: 1;
`;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <Main>
      <MenuBar />
      <Content>{children}</Content>
      <Footer />
    </Main>
  );
};

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user } = useAuth();
  return user ? <>{children}</> : <Navigate to="/login" />;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AppProviders>
        <ThemeProvider theme={defaultTheme}>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: defaultTheme.colors.white,
              color: defaultTheme.colors.blue900,
              border: `1px solid ${defaultTheme.colors.border}`,
            },
            success: {
              iconTheme: {
                primary: defaultTheme.colors.primary,
                secondary: defaultTheme.colors.white,
              },
            },
            error: {
              iconTheme: {
                primary: defaultTheme.colors.error,
                secondary: defaultTheme.colors.white,
              },
            },
          }}
        />
        <Router>
          <Suspense
            fallback={
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                width: '100vw'
              }}>
                <Spinner size="large" tip="Načítání aplikace..." />
              </div>
            }
          >
            <Routes>
              <Route
                path="/"
                element={
                  <Layout>
                    <HomePage />
                  </Layout>
                }
              />
              <Route
                path={`/${MenuItems.CurrentAuction}`}
                element={
                  <Layout>
                    <CurrentAuctionDetail />
                  </Layout>
                }
              />
              <Route
                path={`/${MenuItems.Auctions}`}
                element={
                  <Layout>
                    <Auctions />
                  </Layout>
                }
              />
              <Route
                path={`/${MenuItems.Auctions}/:id`}
                element={
                  <Layout>
                    <AuctionDetail />
                  </Layout>
                }
              />
              <Route
                path={`/${MenuItems.Winners}`}
                element={
                  <Layout>
                    <Winners />
                  </Layout>
                }
              />
              <Route
                path={`/${MenuItems.Winners}/:id`}
                element={
                  <Layout>
                    <AuctionDetail />
                  </Layout>
                }
              />
              <Route
                path={`/${MenuItems.AboutUs}`}
                element={
                  <Layout>
                    <AboutUs />
                  </Layout>
                }
              />
              <Route
                path={`/${MenuItems.Blog}`}
                element={
                  <Layout>
                    <Blog />
                  </Layout>
                }
              />
              <Route
                path={`/${MenuItems.Blog}/:id`}
                element={
                  <Layout>
                    <BlogDetail />
                  </Layout>
                }
              />
              <Route
                path={`/${MenuItems.Help}`}
                element={
                  <Layout>
                    <Help />
                  </Layout>
                }
              />
              <Route
                path={`/${MenuItems.Contact}`}
                element={
                  <Layout>
                    <Contact />
                  </Layout>
                }
              />
              <Route
                path={`/${MenuItems.ShoppingCart}`}
                element={
                  <Layout>
                    <ProtectedRoute>
                      <ShoppingCart />
                    </ProtectedRoute>
                  </Layout>
                }
              />
              {/* Login page*/}
              <Route
                path={`/${MenuItems.LogIn}`}
                element={
                  <Layout>
                    <Login />
                  </Layout>
                }
              />
              {/* Register page*/}
              <Route
                path="/register"
                element={
                  <Layout>
                    <Register />
                  </Layout>
                }
              />
              {/* OAuth callback — BE redirects here with ?session=ok */}
              <Route
                path="/auth/callback"
                element={
                  <Layout>
                    <AuthCallback />
                  </Layout>
                }
              />
              {/* Password reset link target */}
              <Route
                path="/auth/reset-password"
                element={
                  <Layout>
                    <ResetPassword />
                  </Layout>
                }
              />
              {/* Email confirmation link target */}
              <Route
                path="/auth/confirm-email"
                element={
                  <Layout>
                    <ConfirmEmail />
                  </Layout>
                }
              />
              {/* 404 */}
              <Route
                path="*"
                element={
                  <Layout>
                    <NotFound />
                  </Layout>
                }
              />
            </Routes>
          </Suspense>
        </Router>
      </ThemeProvider>
    </AppProviders>
    </QueryClientProvider>
  );
};

export default App;
